import { WebComponent } from '@cian/mf-registry/browser';
import { createRoot, hydrateRoot } from 'react-dom/client';

import { RootApplicationContainer } from '../../shared/common/containers/RootApplicationContainer/RootApplicationContainer';
import { IAppContext } from '../../shared/common/types/applicationContext';
import { TGenericStore } from '../../shared/common/redux';
import {
  setDefaultFormState,
  setEntityId,
  setPageType,
  setSkipOffersFetch,
} from '../../shared/mortgageMiniCalculator/redux';
import { ICalculatorState } from '../../shared/mortgageMiniCalculator/types';

export type TMortgageMiniCalculatorMicrofrontendProps = {
  defaultFormState?: Partial<ICalculatorState['initialFormState']>;
  entityId?: number;
  pageType?: ICalculatorState['pageType'];
};

export function createCustomElementConstructor(context: IAppContext, store: TGenericStore): CustomElementConstructor {
  class MortgageMiniCalculator extends WebComponent<TMortgageMiniCalculatorMicrofrontendProps> {
    private applicationContext: IAppContext;
    private hasRendered = false;

    protected microfrontendName = context.config.getStrict<string>('projectName');
    protected imageVersion = context.config.getStrict<string>('version.commit');
    protected runtimeName = '/mortgage-mini-calculator/';

    public async connectedCallback(): Promise<void> {
      const s = super.connectedCallback();
      this.applicationContext = { ...context, config: context.config.attach({ instanceId: this.instance.id }) };

      return s;
    }

    protected setCalculatorStateByProps() {
      const formStateUpdates: Partial<ICalculatorState['initialFormState']> = {};

      const { defaultFormState, pageType, entityId } = this.props;

      // Обработка параметров initialFormState
      if (defaultFormState) {
        if (defaultFormState.objectCost !== undefined) {
          formStateUpdates.objectCost = defaultFormState.objectCost;
        }
        if (defaultFormState.loanTermYears !== undefined) {
          formStateUpdates.loanTermYears = defaultFormState.loanTermYears;
        }
        if (defaultFormState.downPayment !== undefined) {
          formStateUpdates.downPayment = defaultFormState.downPayment;
        }
        if (defaultFormState.regionId !== undefined) {
          formStateUpdates.regionId = defaultFormState.regionId;
        }
        if (defaultFormState.realEstateType !== undefined) {
          formStateUpdates.realEstateType = defaultFormState.realEstateType;
        }
        if (defaultFormState.objectType !== undefined) {
          formStateUpdates.objectType = defaultFormState.objectType;
        }
        if (defaultFormState.benefitProgram !== undefined) {
          formStateUpdates.benefitProgram = defaultFormState.benefitProgram;
        }
        if (defaultFormState.lifeInsurance !== undefined) {
          formStateUpdates.lifeInsurance = defaultFormState.lifeInsurance;
        }
      }

      if (Object.keys(formStateUpdates).length > 0) {
        store.dispatch(setDefaultFormState(formStateUpdates));
      }

      if (entityId !== undefined) {
        store.dispatch(setEntityId(entityId));
      }

      if (pageType !== undefined) {
        store.dispatch(setPageType(pageType));
      }
    }

    protected componentDidUpdate() {
      this.setCalculatorStateByProps();
    }

    protected componentDidMount() {
      this.setCalculatorStateByProps();
    }

    public async render() {
      if (this.hasRendered) {
        return;
      }
      this.hasRendered = true;

      store.dispatch(setSkipOffersFetch(false));

      const reactElement = <RootApplicationContainer context={this.applicationContext} reduxStore={store} />;

      if (this.shouldHydrate) {
        hydrateRoot(this, reactElement);
      } else {
        createRoot(this).render(reactElement);
      }
    }
  }

  return MortgageMiniCalculator;
}
